import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = "https://ndqudoynysmynztrepwj.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5kcXVkb3lueXNteW56dHJlcHdqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzc0NTk2NzYsImV4cCI6MjA1MzAzNTY3Nn0.NfTdxUlkGfcG2pWxLrlIwEUFIyhE2NosM-71RrEqVrw";

// HMR-safe singleton pattern using window object to survive hot reloads
declare global {
  interface Window {
    __showcaseClient?: SupabaseClient<Database>;
  }
}

// Lazy client getter to ensure true singleton - only initializes when actually accessed
const getShowcaseClient = (): SupabaseClient<Database> => {
  // Check if client already exists on window (survives HMR)
  if (typeof window !== 'undefined' && window.__showcaseClient) {
    return window.__showcaseClient;
  }

  // Create new client instance only when first accessed
  console.log('🎨 [Showcase Client] First-time initialization (lazy-loaded)');
  console.log('🎨 [Showcase Client] Route:', window.location.pathname);
  console.log('🎨 [Showcase Client] Timestamp:', new Date().toISOString());

  const client = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    auth: {
      persistSession: false,  // Don't persist sessions for public routes
      autoRefreshToken: false,  // Don't auto-refresh tokens
      detectSessionInUrl: false,  // Don't detect sessions in URL
      storageKey: 'sb-showcase-client',  // Use unique storage key to avoid conflicts
    },
    global: {
      headers: {
        'X-Client-Info': 'showcase-client',
      },
    },
  });

  // Store on window object to survive HMR
  if (typeof window !== 'undefined') {
    window.__showcaseClient = client;
  }

  console.log('🎨 [Showcase Client] Initialized successfully with minimal auth config');
  return client;
};

// Create a proxy that lazily initializes the client only when accessed
export const showcaseClient = new Proxy({} as SupabaseClient<Database>, {
  get(target, prop) {
    const client = getShowcaseClient();
    const value = client[prop as keyof typeof client];
    return typeof value === 'function' ? value.bind(client) : value;
  }
});
